import React, {
  useState, useEffect, useRef, useCallback, useMemo,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppNav } from '../components/AppNav/AppNav';
import { useTranslation } from 'react-i18next';
import { WorldCanvas, Viewport, WorldCanvasHandle } from '../components/WorldCanvas/WorldCanvas';
import { SpeechBubbles } from '../components/SpeechBubbles';
import { drawSpeechBubbles } from '../utils/speechBubbleCanvas';
import { useSpeechBubbles } from '../hooks/useSpeechBubbles';
import { Point } from '../types/point';
import { PointEvent, HistoryPoint, PlaybackMode } from '../types/history';
import { getHistoryEvents, getHistoryHistogram, getHistorySnapshot } from '../api/history';
import { RootState, AppDispatch } from '../store';
import { fetchColorCatalog } from '../store/colorsSlice';
import { trackEvent } from '../utils/analytics';
import { toastError, toastInfo, toastSuccess } from '../utils/toast';
import {
  createTimelapseRecorder, downloadBlob, canRecordWebM, TimelapseRecorder,
} from '../utils/timelapseRecorder';

// ── helpers ───────────────────────────────────────────────────────────

function toRFC3339(d: Date): string {
  return d.toISOString();
}

function toDatetimeLocal(d: Date): string {
  // datetime-local input expects "YYYY-MM-DDTHH:MM"
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocal(s: string): Date {
  return new Date(s);
}

function viewportToBBox(vp: Viewport, w: number, h: number) {
  const halfW = Math.ceil(w / 2 / vp.zoom) + 2;
  const halfH = Math.ceil(h / 2 / vp.zoom) + 2;
  return {
    x_min: Math.round(vp.x) - halfW,
    y_min: Math.round(vp.y) - halfH,
    x_max: Math.round(vp.x) + halfW,
    y_max: Math.round(vp.y) + halfH,
  };
}

/** World-space rectangle selected for recording. */
interface WorldRect { x_min: number; y_min: number; x_max: number; y_max: number; }

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const nextFrame = () => new Promise<void>(r => requestAnimationFrame(() => r()));

// Watermark burned into exports for non-admin / non-subscribed users. Text
// today; swap-in point for a real logo lives in timelapseRecorder.drawWatermark.
const WATERMARK = { wordmark: 'WE ARE PIXELS', caption: 'wearepixels.ru' };

/** Apply a single event to the mutable points map (in-place). */
function applyEvent(map: Map<number, HistoryPoint>, e: PointEvent) {
  switch (e.event_type) {
    case 'created':
      map.set(e.point_id, { point_id: e.point_id, x: e.x, y: e.y, color: e.color, name: e.name });
      break;
    case 'moved':
      map.set(e.point_id, { point_id: e.point_id, x: e.x, y: e.y, color: e.color, name: e.name });
      break;
    case 'updated':
      if (map.has(e.point_id)) {
        const prev = map.get(e.point_id)!;
        map.set(e.point_id, { ...prev, color: e.color, name: e.name });
      }
      break;
    case 'deleted':
      map.delete(e.point_id);
      break;
    case 'said':
      break; // ephemeral — handled separately via useSpeechBubbles
  }
}

/** Rebuild the points map from snapshot + events[0..upToIndex) */
function rebuildState(
  snapshot: HistoryPoint[],
  events: PointEvent[],
  upToIndex: number,
): Map<number, HistoryPoint> {
  const map = new Map<number, HistoryPoint>();
  for (const p of snapshot) map.set(p.point_id, p);
  for (let i = 0; i < upToIndex; i++) applyEvent(map, events[i]);
  return map;
}

/** Convert HistoryPoint map → Point[] for WorldCanvas */
function toRenderPoints(map: Map<number, HistoryPoint>): Point[] {
  return Array.from(map.values()).map(p => ({
    id: p.point_id,
    user_id: 0,
    name: p.name,
    x: p.x,
    y: p.y,
    color: p.color,
    is_impassable: false,
    created_at: '',
    updated_at: '',
  }));
}

// ── component ─────────────────────────────────────────────────────────

export const HistoryPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();

  // Watermark gating: admins and active subscribers get clean exports.
  const token              = useSelector((s: RootState) => s.auth.token);
  const isAdmin            = useSelector((s: RootState) => s.auth.isAdmin);
  const subscriptionActive = useSelector((s: RootState) => s.colors.subscription.active);
  const watermarked = !isAdmin && !subscriptionActive;

  useEffect(() => {
    if (token) dispatch(fetchColorCatalog());
  }, [token, dispatch]);

  // ── viewport ────────────────────────────────────────────────────────
  const [viewport, setViewport] = useState<Viewport>(() => ({
    x:    parseFloat(searchParams.get('x')    ?? '0'),
    y:    parseFloat(searchParams.get('y')    ?? '0'),
    zoom: parseFloat(searchParams.get('zoom') ?? '4'),
  }));

  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const onResize = () =>
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── time range ──────────────────────────────────────────────────────
  const [fromTime, setFromTime] = useState<Date>(() => {
    const f = searchParams.get('from');
    return f ? new Date(f) : new Date(Date.now() - 24 * 60 * 60 * 1000);
  });
  const [toTime, setToTime] = useState<Date>(() => {
    const t = searchParams.get('to');
    return t ? new Date(t) : new Date();
  });

  // ── playback settings ───────────────────────────────────────────────
  const [mode, setMode] = useState<PlaybackMode>(
    (searchParams.get('mode') as PlaybackMode) ?? 'fixed',
  );
  // fixed: events/sec  |  realtime: time multiplier
  const [speed, setSpeed] = useState<number>(
    parseFloat(searchParams.get('speed') ?? '5'),
  );

  // Autoplay. The checkbox keeps ?play=1 in the synced URL, so a shared link
  // carries it; opening such a link starts playback by itself once the
  // initial auto-load lands (the pending ref makes it a one-shot — pausing
  // later must not restart on every reload).
  const [autoPlay, setAutoPlay] = useState<boolean>(() => searchParams.get('play') === '1');
  const autoPlayPendingRef = useRef(searchParams.get('play') === '1');

  // Setup drawer: open on desktop, collapsed on phones by default.
  const [setupOpen, setSetupOpen] = useState<boolean>(
    () => !window.matchMedia('(max-width: 480px)').matches,
  );

  // ── data ─────────────────────────────────────────────────────────────
  const [shareCopied, setShareCopied] = useState(false);

  const handleShare = useCallback(() => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    });
  }, []);

  const [snapshot, setSnapshot]     = useState<HistoryPoint[]>([]);
  const [events,   setEvents]       = useState<PointEvent[]>([]);
  const [loading,  setLoading]      = useState(false);
  const [error,    setError]        = useState<string | null>(null);
  const [loaded,   setLoaded]       = useState(false);

  // ── playback state ───────────────────────────────────────────────────
  const [eventIndex,     setEventIndex]     = useState(0);
  const [isPlaying,      setIsPlaying]      = useState(false);
  const [currentPoints,  setCurrentPoints]  = useState<Map<number, HistoryPoint>>(new Map());

  // ── recording state ──────────────────────────────────────────────────
  const worldRef       = useRef<WorldCanvasHandle>(null);
  const recorderRef    = useRef<TimelapseRecorder | null>(null);
  const recordingRef   = useRef(false);
  const savedViewport  = useRef<Viewport | null>(null);
  const [recording,    setRecording]    = useState(false);
  const [gifProgress,  setGifProgress]  = useState<number | null>(null);
  // Area selection. Deep-linkable: ?rect=x_min,y_min,x_max,y_max restores the
  // recording window, so a share link reproduces the whole shot setup (area +
  // from/to period + mode/speed), not just the camera position.
  const [selecting, setSelecting]   = useState(false);
  const [recordArea, setRecordArea] = useState<WorldRect | null>(() => {
    const raw = searchParams.get('rect');
    if (!raw) return null;
    const p = raw.split(',').map(Number);
    if (p.length !== 4 || p.some(n => !Number.isFinite(n))) return null;
    return {
      x_min: Math.round(Math.min(p[0], p[2])),
      y_min: Math.round(Math.min(p[1], p[3])),
      x_max: Math.round(Math.max(p[0], p[2])),
      y_max: Math.round(Math.max(p[1], p[3])),
    };
  });
  const [selDrag, setSelDrag]       = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);

  // Current display timestamp (for scrubber label)
  const currentTime = useMemo(() => {
    if (events.length === 0) return fromTime;
    if (eventIndex === 0)    return fromTime;
    return new Date(events[Math.min(eventIndex, events.length) - 1].created_at);
  }, [eventIndex, events, fromTime]);

  // Rendered points for WorldCanvas
  const renderedPoints = useMemo(() => toRenderPoints(currentPoints), [currentPoints]);

  // ── playback engine refs ─────────────────────────────────────────────
  const rafRef          = useRef<number | null>(null);
  const lastFrameRef    = useRef<number>(0);
  const accumulatorRef  = useRef<number>(0); // ms accumulated for fixed mode
  // for realtime mode: reference real-time of when playback started at a given event
  const realtimeStartRef = useRef<{ realMs: number; eventIdx: number; eventMs: number } | null>(null);

  const eventsRef  = useRef(events);
  const snapshotRef = useRef(snapshot);
  const modeRef    = useRef(mode);
  const speedRef   = useRef(speed);
  eventsRef.current   = events;
  snapshotRef.current = snapshot;
  modeRef.current     = mode;
  speedRef.current    = speed;

  // ── Speech bubbles ───────────────────────────────────────────────────
  const { speeches, handleSaidEvent, clearSpeeches } = useSpeechBubbles();
  // Mirrored for the recorder's overlay hook: it draws from inside a RAF
  // callback captured once at start(), so it must not close over stale state.
  const speechesRef       = useRef(speeches);
  const renderedPointsRef = useRef<ReturnType<typeof toRenderPoints>>([]);
  const viewportRef       = useRef(viewport);
  const windowSizeRef     = useRef(windowSize);
  speechesRef.current       = speeches;
  renderedPointsRef.current = renderedPoints;
  viewportRef.current       = viewport;
  windowSizeRef.current     = windowSize;
  // Tracks which event index we last processed for 'said' events, so we
  // only forward NEW said events to the bubble system as playback advances.
  const lastSaidIdxRef = useRef(0);

  // ── load data ────────────────────────────────────────────────────────
  // The data is bounded: a selected recording area wins over the viewport, so
  // replaying one zone never drags the whole world's events over the wire.
  // What the data was loaded FOR is remembered, so panning or zooming away can
  // mark it stale instead of silently replaying an unrelated area.
  const loadedCoverRef = useRef<WorldRect | null>(null);
  const [stale, setStale] = useState(false);
  const [truncatedTotal, setTruncatedTotal] = useState<number | null>(null);

  const PAGE = 10000;      // server-side max per request
  const MAX_EVENTS = 30000; // hard cap: above this playback UX degrades anyway
  const HIST_BUCKETS = 120;

  // ── timeline navigator ───────────────────────────────────────────────
  // The activity strip is the primary time picker. Two separate notions:
  //   view — the window of the time axis the strip currently shows; pan and
  //          zoom move it freely, histogram data loads dynamically for it;
  //   band — the loaded playback range [fromTime, toTime], drawn as a
  //          highlighted band with edge handles. Dragging the band or its
  //          handles changes WHAT plays; dragging empty strip changes only
  //          what you LOOK at. A click inside the band seeks playback.
  const [histView, setHistView] = useState<{ from: number; to: number } | null>(null);
  const histViewRef = useRef(histView);
  histViewRef.current = histView;
  // Histogram cache: counts plus the absolute range they describe, so stale
  // bars keep rendering (stretched) while fresh data for a new view loads.
  // `covered` lists the time ranges actually backed by fetched data — the
  // rest of the view is hatched as "not loaded yet".
  const [histData, setHistData] = useState<{ from: number; to: number; counts: number[]; covered?: Array<[number, number]> } | null>(null);
  const histCanvasRef = useRef<HTMLCanvasElement>(null);
  const histReqRef = useRef(0);
  const histFetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [histLoading, setHistLoading] = useState(false);
  // While a fetch is in flight the hatch over uncovered regions crawls —
  // ~15fps is plenty and cheap.
  const [histAnim, setHistAnim] = useState(0);
  useEffect(() => {
    if (!histLoading) return;
    const id = setInterval(() => setHistAnim(a => a + 1), 66);
    return () => clearInterval(id);
  }, [histLoading]);
  // Taller strip on demand — squinting at a 40px timeline on a phone is no
  // way to pick a range. Preference survives visits.
  const [histTall, setHistTall] = useState(() => {
    try { return localStorage.getItem('historyTimelineTall') === '1'; } catch { return false; }
  });
  const toggleHistTall = () => setHistTall(v => {
    try { localStorage.setItem('historyTimelineTall', v ? '0' : '1'); } catch { /* приватный режим */ }
    return !v;
  });

  const MIN_SPAN = 30_000;                 // zoom-in floor: 30 s
  const MAX_SPAN = 180 * 86_400_000;       // zoom-out ceiling: ~half a year
  const MIN_TIME = Date.UTC(2026, 0, 1);   // nothing exists before the world did
  // Events arrive in pages; this is how many are already in, shown while loading.
  const [loadProgress, setLoadProgress] = useState<number | null>(null);

  // ── histogram tile cache ─────────────────────────────────────────────
  // The curve is fetched in fixed-step, chunk-aligned tiles and kept, so
  // panning back over ground already seen redraws from memory instead of
  // re-asking the server. The bucket step comes from a ladder (one zoom
  // level shares one lattice), a chunk is HIST_BUCKETS buckets of that step,
  // and the server's buckets land exactly on the lattice because requests are
  // chunk-aligned. Keyed by bbox — a different area is a different curve.
  const HIST_STEPS = [250, 500, 1e3, 2e3, 5e3, 1e4, 15e3, 3e4, 6e4, 12e4, 3e5, 6e5, 9e5, 18e5, 36e5, 72e5, 108e5, 216e5, 432e5, 864e5, 1728e5];
  const histCacheRef = useRef<{
    sig: string;
    counts: Map<number, Map<number, number>>; // step → global bucket idx → n
    chunks: Map<number, Map<number, number>>; // step → chunk idx → expiresAt (Infinity = final)
  }>({ sig: '', counts: new Map(), chunks: new Map() });
  // Tiles currently in flight (`step:chunk`) — a settle must not re-request a
  // tile an earlier settle is already fetching.
  const histPendingRef = useRef<Set<string>>(new Set());

  const histStepFor = (span: number) =>
    HIST_STEPS.find(s => s >= span / HIST_BUCKETS) ?? HIST_STEPS[HIST_STEPS.length - 1];

  // Drawable data for the view, assembled from cached tiles. Null when the
  // cache holds nothing on this lattice — the caller then keeps stale bars.
  const assembleHist = (view: { from: number; to: number }) => {
    const step = histStepFor(view.to - view.from);
    const chunkSpan = step * HIST_BUCKETS;
    const chunkMap = histCacheRef.current.chunks.get(step);
    if (!chunkMap || chunkMap.size === 0) return null;
    const i0 = Math.floor(view.from / step), i1 = Math.ceil(view.to / step);
    const c0 = Math.floor(i0 / HIST_BUCKETS), c1 = Math.floor((i1 - 1) / HIST_BUCKETS);
    const covered: Array<[number, number]> = [];
    for (let c = c0; c <= c1; c++) {
      if (!chunkMap.has(c)) continue;
      const s = c * chunkSpan, e = s + chunkSpan;
      if (covered.length && covered[covered.length - 1][1] === s) covered[covered.length - 1][1] = e;
      else covered.push([s, e]);
    }
    if (!covered.length) return null;
    const counts = new Array<number>(i1 - i0).fill(0);
    const cm = histCacheRef.current.counts.get(step);
    if (cm) for (let i = i0; i < i1; i++) counts[i - i0] = cm.get(i) ?? 0;
    return { from: i0 * step, to: i1 * step, counts, covered };
  };

  // Histogram for an arbitrary view window. Served fully server-side; a curve
  // built from the capped 30k loaded events would show the cap, not activity.
  // Only tiles missing from the cache are requested.
  const fetchHist = useCallback((view: { from: number; to: number } | null, bboxOverride?: ReturnType<typeof viewportToBBox>) => {
    if (!view) return;
    const bbox = bboxOverride ?? loadedCoverRef.current ?? viewportToBBox(viewport, windowSize.width, windowSize.height);
    const sig = `${bbox.x_min}:${bbox.y_min}:${bbox.x_max}:${bbox.y_max}`;
    const cache = histCacheRef.current;
    if (cache.sig !== sig) { cache.sig = sig; cache.counts.clear(); cache.chunks.clear(); }

    const step = histStepFor(view.to - view.from);
    const chunkSpan = step * HIST_BUCKETS;
    const now = Date.now();
    let chunkMap = cache.chunks.get(step);
    if (!chunkMap) { chunkMap = new Map(); cache.chunks.set(step, chunkMap); }
    let cm = cache.counts.get(step);
    if (!cm) { cm = new Map(); cache.counts.set(step, cm); }

    const i0 = Math.floor(view.from / step), i1 = Math.ceil(view.to / step);
    const c0 = Math.floor(i0 / HIST_BUCKETS), c1 = Math.floor((i1 - 1) / HIST_BUCKETS);
    const pend = histPendingRef.current;
    const missing: number[] = [];
    let inFlight = false;
    for (let c = c0; c <= c1; c++) {
      const exp = chunkMap.get(c);
      if (exp !== undefined && exp > now) continue;
      if (pend.has(`${step}:${c}`)) { inFlight = true; continue; }
      const s = c * chunkSpan, e = s + chunkSpan;
      if (e <= MIN_TIME) { chunkMap.set(c, Infinity); continue; } // before the world existed: empty forever
      if (s > now) { chunkMap.set(c, s); continue; }              // the future: empty until it happens
      missing.push(c);
    }

    // Show what the cache already holds straight away.
    const ready = assembleHist(view);
    if (ready) setHistData(ready);

    if (!missing.length) { if (!inFlight) setHistLoading(false); return; }
    const req = ++histReqRef.current;
    setHistLoading(true);
    Promise.all(missing.map(c => {
      const s = c * chunkSpan, e = s + chunkSpan;
      pend.add(`${step}:${c}`);
      return getHistoryHistogram({
        ...bbox,
        from: toRFC3339(new Date(s)),
        to:   toRFC3339(new Date(e)),
        buckets: HIST_BUCKETS,
      }).then(res => {
        for (const hb of res.data.hist ?? []) {
          if (hb.b >= 0 && hb.b < HIST_BUCKETS) cm!.set(c * HIST_BUCKETS + hb.b, hb.n);
        }
        // A chunk still in progress when fetched keeps growing — let it expire
        // so the live edge refreshes on the next settle. Events are append-only,
        // so re-merging can only overwrite counts upward.
        chunkMap!.set(c, e <= now ? Infinity : now + 30_000);
        // Redraw with whatever view is current — a slow tile may land after
        // the user has already panned on.
        const a = assembleHist(histViewRef.current ?? view);
        if (a) setHistData(a);
      }).finally(() => pend.delete(`${step}:${c}`));
    })).then(() => {
      if (histReqRef.current === req) setHistLoading(false);
    }).catch(() => {
      if (histReqRef.current === req) setHistLoading(false);
    });
  }, [viewport, windowSize]); // eslint-disable-line

  // Pan/zoom settled → fetch the curve for the new window, debounced so a
  // fling does not fire a request per frame. Cached ground redraws instantly,
  // without waiting for the debounce, let alone the network.
  useEffect(() => {
    if (!histView) return;
    const a = assembleHist(histView);
    if (a) setHistData(a);
    if (histFetchTimer.current) clearTimeout(histFetchTimer.current);
    histFetchTimer.current = setTimeout(() => fetchHist(histView), 300);
    return () => { if (histFetchTimer.current) clearTimeout(histFetchTimer.current); };
  }, [histView, fetchHist]); // eslint-disable-line

  const loadData = useCallback(async (range?: { from: Date; to: Date }) => {
    setLoading(true);
    setError(null);
    setIsPlaying(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    const bbox = recordArea ?? viewportToBBox(viewport, windowSize.width, windowSize.height);
    const fromD = range?.from ?? fromTime;
    const toD   = range?.to   ?? toTime;
    if (range) { setFromTime(range.from); setToTime(range.to); }

    try {
      const from = toRFC3339(fromD);
      const to   = toRFC3339(toD);

      // The strip's view: an explicit (button/auto) load re-centres it on the
      // loaded range with some margin, so the band sits inside the picture; a
      // band-edit reload keeps whatever the user was looking at.
      if (!range) {
        const span = toD.getTime() - fromD.getTime();
        setHistView({ from: fromD.getTime() - span * 0.15, to: toD.getTime() + span * 0.15 });
      } else {
        fetchHist(histViewRef.current, bbox); // bbox may have changed since load
      }

      const [snapRes, firstPage] = await Promise.all([
        getHistorySnapshot({ ...bbox, at: from }),
        getHistoryEvents({ ...bbox, from, to, limit: PAGE }),
      ]);

      const total = firstPage.data.total ?? 0;
      let evts: PointEvent[] = firstPage.data.events ?? [];
      setLoadProgress(evts.length);
      // Page until the range is complete or the cap is hit — a silent cut at
      // one page replayed only the first minutes of a dense range.
      while (evts.length < Math.min(total, MAX_EVENTS)) {
        const page = await getHistoryEvents({ ...bbox, from, to, limit: PAGE, offset: evts.length });
        const got = page.data.events ?? [];
        if (got.length === 0) break;
        evts = evts.concat(got);
        setLoadProgress(evts.length);
      }
      setTruncatedTotal(total > evts.length ? total : null);

      const snap = snapRes.data.points ?? [];
      setSnapshot(snap);
      setEvents(evts);
      setEventIndex(0);
      setCurrentPoints(rebuildState(snap, evts, 0));
      setLoaded(true);
      loadedCoverRef.current = bbox;
      setStale(false);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? t('history.loadError'));
    } finally {
      setLoading(false);
      setLoadProgress(null);
    }
  }, [viewport, windowSize, fromTime, toTime, recordArea]);

  // ── timeline: rendering ──────────────────────────────────────────────
  useEffect(() => {
    const cv = histCanvasRef.current;
    if (!cv || !histView) return;
    const dpr = window.devicePixelRatio || 1;
    const w = cv.clientWidth, h = cv.clientHeight;
    if (cv.width !== w * dpr || cv.height !== h * dpr) { cv.width = w * dpr; cv.height = h * dpr; }
    const g = cv.getContext('2d')!;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, w, h);

    const span = histView.to - histView.from;
    const tx = (t: number) => ((t - histView.from) / span) * w;

    // Bars: stale data keeps rendering, mapped to absolute time, while a fresh
    // window loads — panning shows the world sliding, not a blank strip.
    if (histData) {
      const step = (histData.to - histData.from) / histData.counts.length;
      const max = Math.max(1, ...histData.counts);
      g.fillStyle = 'rgba(140, 190, 255, 0.55)';
      for (let i = 0; i < histData.counts.length; i++) {
        const n = histData.counts[i];
        if (n === 0) continue;
        const x0 = tx(histData.from + i * step);
        const x1 = tx(histData.from + (i + 1) * step);
        if (x1 < 0 || x0 > w) continue;
        const bh = Math.max(1, Math.sqrt(n / max) * (h - 13));
        g.fillRect(x0, h - 12 - bh, Math.max(1, x1 - x0 - 0.5), bh);
      }
    }

    // Regions of the view not covered by the data at hand: hatched, so it is
    // obvious which stretch of the curve is still being fetched after a pan
    // or zoom rather than genuinely empty.
    {
      const gaps: Array<[number, number]> = [];
      if (!histData) gaps.push([0, w]);
      else {
        // Complement of the covered ranges within the view (tiles may leave
        // holes in the middle, not just at the edges).
        const cov = histData.covered ?? [[histData.from, histData.to] as [number, number]];
        let cur = 0;
        for (const [s, e] of cov) {
          const x0 = Math.max(0, Math.min(w, tx(s)));
          const x1 = Math.max(0, Math.min(w, tx(e)));
          if (x0 > cur) gaps.push([cur, x0]);
          cur = Math.max(cur, x1);
        }
        if (cur < w) gaps.push([cur, w]);
      }
      if (gaps.length) {
        const phase = histLoading ? (performance.now() / 40) % 14 : 0;
        g.save();
        for (const [x0, x1] of gaps) {
          if (x1 - x0 < 1) continue;
          g.beginPath(); g.rect(x0, 0, x1 - x0, h - 11); g.clip();
          g.fillStyle = 'rgba(140, 190, 255, 0.05)';
          g.fillRect(x0, 0, x1 - x0, h - 11);
          g.strokeStyle = 'rgba(140, 190, 255, 0.18)';
          for (let x = x0 - h - phase; x < x1 + h; x += 14) {
            g.beginPath(); g.moveTo(x, h - 11); g.lineTo(x + h, -h); g.stroke();
          }
        }
        g.restore();
      }
    }

    // Time ticks: pick the smallest step that keeps labels ~70px apart.
    const LADDER = [60e3, 5 * 60e3, 15 * 60e3, 30 * 60e3, 36e5, 3 * 36e5, 6 * 36e5, 12 * 36e5, 864e5, 7 * 864e5, 30 * 864e5];
    const step = LADDER.find(st => (st / span) * w >= 70) ?? LADDER[LADDER.length - 1];
    g.font = '9px monospace';
    g.textAlign = 'center';
    for (let t = Math.ceil(histView.from / step) * step; t <= histView.to; t += step) {
      const x = tx(t);
      g.strokeStyle = 'rgba(255,255,255,0.08)';
      g.beginPath(); g.moveTo(x, 0); g.lineTo(x, h - 11); g.stroke();
      const d = new Date(t);
      const label = step >= 864e5
        ? `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`
        : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      g.fillStyle = 'rgba(255,255,255,0.45)';
      g.fillText(label, x, h - 2);
    }

    // The band: the loaded playback range, with edge handles.
    const bx0 = tx(fromTime.getTime()), bx1 = tx(toTime.getTime());
    if (bx1 > 0 && bx0 < w) {
      g.fillStyle = 'rgba(255, 200, 80, 0.16)';
      g.fillRect(bx0, 0, bx1 - bx0, h - 11);
      g.strokeStyle = 'rgba(255, 200, 80, 0.85)';
      g.strokeRect(bx0 + 0.5, 0.5, bx1 - bx0 - 1, h - 12);
      g.fillStyle = 'rgba(255, 200, 80, 0.9)';
      g.fillRect(bx0 - 2, 0, 4, h - 11);
      g.fillRect(bx1 - 2, 0, 4, h - 11);
    }

    // Full timestamps of the view's edges, top corners. The right one leaves
    // room for the expand toggle sitting over the canvas.
    {
      const fmt = (t: number) => {
        const d = new Date(t);
        const p2 = (n: number) => String(n).padStart(2, '0');
        const base = `${p2(d.getDate())}.${p2(d.getMonth() + 1)}.${d.getFullYear()} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
        return span < 10 * 60e3 ? `${base}:${p2(d.getSeconds())}` : base;
      };
      g.font = '9px monospace';
      const put = (text: string, x: number, align: CanvasTextAlign) => {
        g.textAlign = align;
        const tw = g.measureText(text).width;
        g.fillStyle = 'rgba(10, 10, 16, 0.75)';
        g.fillRect(align === 'left' ? x - 3 : x - tw - 3, 2, tw + 6, 12);
        g.fillStyle = 'rgba(255, 255, 255, 0.6)';
        g.fillText(text, x, 11);
      };
      put(fmt(histView.from), 4, 'left');
      put(fmt(histView.to), w - (w < 500 ? 42 : 34), 'right');
    }

    // Playhead.
    if (loaded) {
      const px = tx(currentTime.getTime());
      if (px >= 0 && px <= w) {
        g.strokeStyle = 'rgba(255,255,255,0.9)';
        g.beginPath(); g.moveTo(px, 0); g.lineTo(px, h - 11); g.stroke();
      }
    }
  }, [histData, histView, fromTime, toTime, currentTime, loaded, histTall, histLoading, histAnim]);

  // ── timeline: gestures ───────────────────────────────────────────────
  // One persistent gesture record; handlers are recreated every render but
  // the gesture must survive across renders mid-drag.
  const gestRef = useRef<{
    mode: 'pan' | 'left' | 'right' | 'band' | 'pinch' | null;
    startX: number; moved: boolean;
    view0: { from: number; to: number };
    band0: { f: number; t: number };
    cur:   { f: number; t: number };
    ptrs: Map<number, number>;
    pinch0?: { d: number; center: number; span: number };
    pressTimer?: ReturnType<typeof setTimeout>;
    longFired?: boolean;
  }>({ mode: null, startX: 0, moved: false, view0: { from: 0, to: 0 }, band0: { f: 0, t: 0 }, cur: { f: 0, t: 0 }, ptrs: new Map() });

  const clampView = (v: { from: number; to: number }) => {
    let span = clamp(v.to - v.from, MIN_SPAN, MAX_SPAN);
    let from = v.from, to = from + span;
    const maxTo = Date.now() + span * 0.1;
    if (to > maxTo) { to = maxTo; from = to - span; }
    if (from < MIN_TIME) { from = MIN_TIME; to = from + span; }
    return { from, to };
  };

  // Bring the band HERE: keep its duration, centre it at t, load. This is the
  // way back when the band has been panned off the visible window.
  const placeBandAt = (t: number) => {
    const dur = Math.max(MIN_SPAN, toTime.getTime() - fromTime.getTime());
    let from = t - dur / 2, to = t + dur / 2;
    const maxTo = Date.now();
    if (to > maxTo) { to = maxTo; from = to - dur; }
    if (from < MIN_TIME) { from = MIN_TIME; to = from + dur; }
    try { navigator.vibrate?.(30); } catch { /* не везде есть */ }
    loadData({ from: new Date(from), to: new Date(to) });
  };

  const stripX = (e: React.PointerEvent) => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    return { x: e.clientX - r.left, w: r.width };
  };

  const onHistDown = (e: React.PointerEvent) => {
    const view = histViewRef.current;
    if (!view) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const g = gestRef.current;
    const { x, w } = stripX(e);
    g.ptrs.set(e.pointerId, e.clientX);
    if (g.ptrs.size === 2) {
      const vals = Array.from(g.ptrs.values()); const a = vals[0], b = vals[1];
      g.mode = 'pinch';
      g.pinch0 = { d: Math.max(8, Math.abs(a - b)), center: view.from + (x / w) * (view.to - view.from), span: view.to - view.from };
      return;
    }
    const span = view.to - view.from;
    const tx = (t: number) => ((t - view.from) / span) * w;
    const bx0 = tx(fromTime.getTime()), bx1 = tx(toTime.getTime());
    g.startX = x; g.moved = false;
    g.view0 = { ...view };
    g.band0 = { f: fromTime.getTime(), t: toTime.getTime() };
    g.cur = { ...g.band0 };
    if (Math.abs(x - bx0) < 12)      g.mode = 'left';
    else if (Math.abs(x - bx1) < 12) g.mode = 'right';
    else if (x > bx0 && x < bx1)     g.mode = 'band';
    else                             g.mode = 'pan';

    // Long press = place the band at this moment. Armed for pan/band only:
    // holding a handle means resizing, not summoning.
    g.longFired = false;
    if (g.pressTimer) clearTimeout(g.pressTimer);
    if (g.mode === 'pan' || g.mode === 'band') {
      const pressT = view.from + (x / w) * span;
      g.pressTimer = setTimeout(() => {
        if (g.moved || g.mode === 'pinch') return;
        g.longFired = true;
        g.mode = null;
        placeBandAt(pressT);
      }, 500);
    }
  };

  const onHistMove = (e: React.PointerEvent) => {
    const g = gestRef.current;
    if (!g.mode) return;
    const { x, w } = stripX(e);
    if (g.ptrs.has(e.pointerId)) g.ptrs.set(e.pointerId, e.clientX);

    if (g.mode === 'pinch' && g.pinch0 && g.ptrs.size >= 2) {
      const vals = Array.from(g.ptrs.values()); const a = vals[0], b = vals[1];
      const d = Math.max(8, Math.abs(a - b));
      const span = clamp(g.pinch0.span * (g.pinch0.d / d), MIN_SPAN, MAX_SPAN);
      const frac = clamp((g.pinch0.center - histViewRef.current!.from) / (histViewRef.current!.to - histViewRef.current!.from), 0, 1);
      setHistView(clampView({ from: g.pinch0.center - frac * span, to: g.pinch0.center + (1 - frac) * span }));
      return;
    }

    const span = g.view0.to - g.view0.from;
    const dt = ((x - g.startX) / w) * span;
    if (Math.abs(x - g.startX) > 3) {
      g.moved = true;
      if (g.pressTimer) { clearTimeout(g.pressTimer); g.pressTimer = undefined; }
    }

    if (g.mode === 'pan') {
      setHistView(clampView({ from: g.view0.from - dt, to: g.view0.to - dt }));
    } else if (g.mode === 'band') {
      g.cur = { f: g.band0.f + dt, t: g.band0.t + dt };
      setFromTime(new Date(g.cur.f)); setToTime(new Date(g.cur.t));
    } else if (g.mode === 'left') {
      g.cur = { f: Math.min(g.band0.f + dt, g.band0.t - MIN_SPAN), t: g.band0.t };
      setFromTime(new Date(g.cur.f));
    } else if (g.mode === 'right') {
      g.cur = { f: g.band0.f, t: Math.max(g.band0.t + dt, g.band0.f + MIN_SPAN) };
      setToTime(new Date(g.cur.t));
    }
  };

  const onHistUp = (e: React.PointerEvent) => {
    const g = gestRef.current;
    g.ptrs.delete(e.pointerId);
    if (g.pressTimer) { clearTimeout(g.pressTimer); g.pressTimer = undefined; }
    if (g.longFired) { g.longFired = false; return; } // жест уже израсходован
    if (!g.mode) return;
    const mode = g.mode;
    if (g.ptrs.size > 0) return;   // second finger still down
    g.mode = null;
    // A clean click — wherever it lands, band or empty strip — seeks playback
    // to that moment when it falls inside the loaded range.
    if (!g.moved && (mode === 'pan' || mode === 'band')) {
      if (loaded && events.length > 0) {
        const view = histViewRef.current!;
        const { x, w } = stripX(e);
        const t = view.from + (x / w) * (view.to - view.from);
        if (t >= fromTime.getTime() && t <= toTime.getTime()) {
          let lo = 0, hi = events.length;
          while (lo < hi) {
            const mid = (lo + hi) >> 1;
            if (new Date(events[mid].created_at).getTime() < t) lo = mid + 1; else hi = mid;
          }
          scrubToIndex(lo);
        }
      }
      return;
    }
    if (mode === 'pinch' || mode === 'pan') return;
    // Band or handle released after a real drag → that IS the new range.
    if (g.moved) loadData({ from: new Date(g.cur.f), to: new Date(g.cur.t) });
  };

  // Wheel zoom, anchored at the cursor. Attached manually: React wheel
  // handlers are passive and cannot preventDefault page scroll.
  useEffect(() => {
    const cv = histCanvasRef.current;
    if (!cv) return;
    const onWheel = (e: WheelEvent) => {
      const view = histViewRef.current;
      if (!view) return;
      e.preventDefault();
      const r = cv.getBoundingClientRect();
      const frac = clamp((e.clientX - r.left) / r.width, 0, 1);
      const anchor = view.from + frac * (view.to - view.from);
      const span = clamp((view.to - view.from) * (e.deltaY > 0 ? 1.25 : 0.8), MIN_SPAN, MAX_SPAN);
      setHistView(cl => {
        const v = { from: anchor - frac * span, to: anchor + (1 - frac) * span };
        let { from, to } = v;
        const maxTo = Date.now() + span * 0.1;
        if (to > maxTo) { to = maxTo; from = to - span; }
        if (from < MIN_TIME) { from = MIN_TIME; to = from + span; }
        return { from, to };
      });
    };
    cv.addEventListener('wheel', onWheel, { passive: false });
    return () => cv.removeEventListener('wheel', onWheel);
  }, [histView === null]); // eslint-disable-line

  // Load once on open, on every layout. Waiting for an explicit press meant
  // an empty world until you found the button — which on mobile hides inside
  // the collapsed setup drawer. Deliberately once: re-loading on pan would
  // throw away the playback position, which the stale hint already covers.
  const autoLoadedRef = useRef(false);
  useEffect(() => {
    if (autoLoadedRef.current) return;
    if (windowSize.width === 0 || windowSize.height === 0) return;
    autoLoadedRef.current = true;
    loadData();
  }, [windowSize, loadData]);

  // Mark loaded data stale once the view leaves what it was loaded for: either
  // the viewport slid off the covered rectangle, or it zoomed in so far that
  // the loaded area is an order of magnitude bigger than what is on screen.
  useEffect(() => {
    const cover = loadedCoverRef.current;
    if (!cover || !loaded) return;
    // With a recording window selected the load is pinned to it, so the camera
    // position is irrelevant — only editing the window itself outdates the data.
    if (recordArea) {
      setStale(
        recordArea.x_min !== cover.x_min || recordArea.x_max !== cover.x_max ||
        recordArea.y_min !== cover.y_min || recordArea.y_max !== cover.y_max,
      );
      return;
    }
    const vp = viewportToBBox(viewport, windowSize.width, windowSize.height);
    const slidOff = vp.x_min < cover.x_min - 2 || vp.x_max > cover.x_max + 2 ||
                    vp.y_min < cover.y_min - 2 || vp.y_max > cover.y_max + 2;
    const coverArea = (cover.x_max - cover.x_min) * (cover.y_max - cover.y_min);
    const vpArea    = (vp.x_max - vp.x_min) * (vp.y_max - vp.y_min);
    const zoomedWayIn = vpArea > 0 && coverArea / vpArea > 9;
    setStale(slidOff || zoomedWayIn);
  }, [viewport, windowSize, loaded, recordArea]);

  // ── scrub to index ───────────────────────────────────────────────────
  const scrubToIndex = useCallback((idx: number) => {
    const clamped = Math.max(0, Math.min(idx, eventsRef.current.length));
    setEventIndex(clamped);
    setCurrentPoints(rebuildState(snapshotRef.current, eventsRef.current, clamped));
    realtimeStartRef.current = null; // reset realtime anchor on manual scrub
    lastSaidIdxRef.current = clamped; // don't re-fire said events on scrub
    clearSpeeches();
  }, [clearSpeeches]);

  // ── animation frame ──────────────────────────────────────────────────
  const tick = useCallback((now: number) => {
    const evts  = eventsRef.current;
    const mode  = modeRef.current;
    const speed = speedRef.current;

    // Keep the recording output surface fresh with the latest rendered frame.
    if (recordingRef.current) recorderRef.current?.blitFrame();

    setEventIndex(prevIdx => {
      if (prevIdx >= evts.length) {
        setIsPlaying(false);
        return prevIdx;
      }

      let newIdx = prevIdx;

      if (mode === 'fixed') {
        const elapsed = now - lastFrameRef.current;
        lastFrameRef.current = now;
        accumulatorRef.current += elapsed;
        const msPerEvent = 1000 / speed;
        const steps = Math.floor(accumulatorRef.current / msPerEvent);
        accumulatorRef.current -= steps * msPerEvent;
        newIdx = Math.min(prevIdx + steps, evts.length);
      } else {
        // realtime mode
        if (!realtimeStartRef.current) {
          realtimeStartRef.current = {
            realMs:   now,
            eventIdx: prevIdx,
            eventMs:  new Date(evts[prevIdx]?.created_at ?? evts[0].created_at).getTime(),
          };
        }
        const ref = realtimeStartRef.current;
        const elapsedReal = now - ref.realMs;
        const targetMs    = ref.eventMs + elapsedReal * speed;
        // Advance while event timestamp <= targetMs
        let i = ref.eventIdx;
        while (i < evts.length && new Date(evts[i].created_at).getTime() <= targetMs) i++;
        newIdx = i;
      }

      if (newIdx !== prevIdx) {
        // Apply only the new events (don't rebuild from scratch)
        setCurrentPoints(prev => {
          const next = new Map(prev);
          for (let i = prevIdx; i < newIdx; i++) applyEvent(next, evts[i]);
          return next;
        });
      }

      return newIdx;
    });

    rafRef.current = requestAnimationFrame(tick);
  }, []);

  // ── play / pause ─────────────────────────────────────────────────────
  useEffect(() => {
    if (isPlaying) {
      lastFrameRef.current   = performance.now();
      accumulatorRef.current = 0;
      realtimeStartRef.current = null;
      rafRef.current = requestAnimationFrame(tick);
    } else {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isPlaying, tick]);

  // Deep-linked autoplay: once the first load has produced a playable range,
  // press play on the visitor's behalf.
  useEffect(() => {
    if (!loaded || !autoPlayPendingRef.current) return;
    autoPlayPendingRef.current = false;
    setIsPlaying(true);
  }, [loaded]);

  // Auto-pause at end
  useEffect(() => {
    if (eventIndex >= events.length && events.length > 0 && isPlaying) {
      setIsPlaying(false);
    }
  }, [eventIndex, events.length, isPlaying]);

  // ── Forward 'said' events to speech bubbles as playback advances ─────
  // Runs whenever eventIndex changes (each animation frame during playback).
  // Only processes events between lastSaidIdxRef and the current index so
  // we never re-fire a bubble that has already been shown.
  useEffect(() => {
    const start = lastSaidIdxRef.current;
    const end   = eventIndex;
    for (let i = start; i < end && i < events.length; i++) {
      if (events[i].event_type === 'said') {
        // Text is stored in the `name` column for said events.
        handleSaidEvent(events[i].point_id, events[i].name, events[i].style);
      }
    }
    lastSaidIdxRef.current = eventIndex;
  }, [eventIndex]);

  // ── sync URL params ──────────────────────────────────────────────────
  useEffect(() => {
    const p: Record<string, string> = {
      x:     String(Math.round(viewport.x)),
      y:     String(Math.round(viewport.y)),
      zoom:  String(viewport.zoom),
      from:  toRFC3339(fromTime),
      to:    toRFC3339(toTime),
      mode,
      speed: String(speed),
    };
    if (recordArea) {
      p.rect = [recordArea.x_min, recordArea.y_min, recordArea.x_max, recordArea.y_max]
        .map(Math.round).join(',');
    }
    if (autoPlay) p.play = '1';
    setSearchParams(p, { replace: true });
  }, [viewport, fromTime, toTime, mode, speed, recordArea, autoPlay]); // eslint-disable-line

  // ── derived UI values ────────────────────────────────────────────────
  const totalMs    = toTime.getTime() - fromTime.getTime();
  const currentMs  = currentTime.getTime() - fromTime.getTime();
  const progress   = totalMs > 0 ? Math.max(0, Math.min(1, currentMs / totalMs)) : 0;

  const handleScrubberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const ratio = parseFloat(e.target.value) / 1000;
    if (events.length === 0) return;
    const targetMs = fromTime.getTime() + ratio * totalMs;
    // Find event index for target time
    let idx = 0;
    while (idx < events.length && new Date(events[idx].created_at).getTime() <= targetMs) idx++;
    scrubToIndex(idx);
  };

  const togglePlay = () => {
    if (!loaded) { loadData(); return; }
    if (eventIndex >= events.length) scrubToIndex(0);
    setIsPlaying(p => !p);
  };

  const handleReset = () => {
    setIsPlaying(false);
    scrubToIndex(0);
  };

  const speedOptions = mode === 'fixed'
    ? [1, 2, 5, 10, 20, 50]
    : [0.5, 1, 2, 5, 10, 50, 100];

  // ── area selection (drag a rectangle on the field) ───────────────────
  const screenToWorld = useCallback((sx: number, sy: number) => {
    const { x, y, zoom } = viewport;
    return {
      wx: (sx - windowSize.width / 2) / zoom + x,
      wy: y - (sy - windowSize.height / 2) / zoom,
    };
  }, [viewport, windowSize]);

  const onSelPointerDown = (e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    setSelDrag({ x0: e.clientX, y0: e.clientY, x1: e.clientX, y1: e.clientY });
  };
  const onSelPointerMove = (e: React.PointerEvent) => {
    setSelDrag(d => (d ? { ...d, x1: e.clientX, y1: e.clientY } : d));
  };
  const onSelPointerUp = () => {
    setSelDrag(d => {
      if (d) {
        const a = screenToWorld(Math.min(d.x0, d.x1), Math.min(d.y0, d.y1));
        const b = screenToWorld(Math.max(d.x0, d.x1), Math.max(d.y0, d.y1));
        // note: screen-y inverts world-y, so recompute min/max in world space.
        // Snap outward to whole cells: the box goes into the history API as a
        // bbox, and the server parses coordinates with Atoi — a fractional
        // value silently falls back to the ±50 default box.
        const area: WorldRect = {
          x_min: Math.floor(Math.min(a.wx, b.wx)),
          x_max: Math.ceil(Math.max(a.wx, b.wx)),
          y_min: Math.floor(Math.min(a.wy, b.wy)),
          y_max: Math.ceil(Math.max(a.wy, b.wy)),
        };
        if (Math.abs(d.x1 - d.x0) > 8 && Math.abs(d.y1 - d.y0) > 8) {
          setRecordArea(area);
        }
      }
      return null;
    });
    setSelecting(false);
  };

  // ── record-frame editing: drag handles resize, the label moves ───────
  // Handles are the only interactive parts of the frame (its body stays
  // pointer-events: none), so panning the field through the window is intact.
  // Compass is world-oriented: 'n' is the top of the screen = larger world Y.
  const frameDragRef = useRef<{ h: string; sx: number; sy: number; orig: WorldRect } | null>(null);

  const onFrameHandleDown = (h: string) => (e: React.PointerEvent) => {
    if (recording || !recordArea) return;
    e.stopPropagation();
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    frameDragRef.current = { h, sx: e.clientX, sy: e.clientY, orig: recordArea };
  };
  const onFrameHandleMove = (e: React.PointerEvent) => {
    const d = frameDragRef.current;
    if (!d) return;
    const dx = Math.round((e.clientX - d.sx) / viewport.zoom);
    const dy = Math.round(-(e.clientY - d.sy) / viewport.zoom); // screen y is world −y
    let { x_min, y_min, x_max, y_max } = d.orig;
    if (d.h === 'move') {
      x_min += dx; x_max += dx; y_min += dy; y_max += dy;
    } else {
      if (d.h.includes('w')) x_min = Math.min(x_min + dx, x_max);
      if (d.h.includes('e')) x_max = Math.max(x_max + dx, x_min);
      if (d.h.includes('s')) y_min = Math.min(y_min + dy, y_max);
      if (d.h.includes('n')) y_max = Math.max(y_max + dy, y_min);
    }
    setRecordArea(prev => {
      if (prev && prev.x_min === x_min && prev.x_max === x_max &&
          prev.y_min === y_min && prev.y_max === y_max) return prev;
      return { x_min, y_min, x_max, y_max };
    });
  };
  const onFrameHandleUp = () => { frameDragRef.current = null; };

  const fitViewportToArea = useCallback((area: WorldRect): Viewport => {
    const cx = (area.x_min + area.x_max) / 2;
    const cy = (area.y_min + area.y_max) / 2;
    const worldW = Math.max(1, area.x_max - area.x_min + 1);
    const worldH = Math.max(1, area.y_max - area.y_min + 1);
    const zoom = Math.min(windowSize.width / worldW, windowSize.height / worldH);
    return { x: cx, y: cy, zoom: clamp(zoom, 0.5, 1000) };
  }, [windowSize]);

  // ── recording ────────────────────────────────────────────────────────
  const finishRecording = useCallback(async (save: boolean) => {
    const rec = recorderRef.current;
    recordingRef.current = false;
    setRecording(false);
    setIsPlaying(false);
    recorderRef.current = null;

    // Restore the viewport if we reframed it for an area recording.
    if (savedViewport.current) {
      setViewport(savedViewport.current);
      savedViewport.current = null;
    }

    if (!rec) return;
    if (!save) { rec.cancel(); setGifProgress(null); return; }

    try {
      if (rec.format === 'gif') setGifProgress(0);
      const blob = await rec.stop();
      const stamp = fromTime.toISOString().slice(0, 19).replace(/[:T]/g, '-');
      downloadBlob(blob, `wearepixels-timelapse-${stamp}.${rec.ext}`);
      toastSuccess(t('history.recordSaved'));
    } catch (e) {
      toastError(t('history.recordFailed'));
    } finally {
      setGifProgress(null);
    }
  }, [fromTime, t]);

  // When playback ends (auto-pause) while recording, finalize + save.
  useEffect(() => {
    if (recordingRef.current && !isPlaying) {
      finishRecording(true);
    }
  }, [isPlaying, finishRecording]);

  const startRecording = useCallback(async () => {
    if (!loaded || events.length === 0) { toastInfo(t('history.recordLoadFirst')); return; }
    const source = worldRef.current?.getCanvas();
    if (!source) { toastError(t('history.recordUnsupported')); return; }

    // Reframe onto the selected area (the renderer culls to the viewport, so an
    // off-screen area must be brought into view before capture).
    if (recordArea) {
      savedViewport.current = viewport;
      setViewport(fitViewportToArea(recordArea));
    }
    // Rewind to the start and let the reframed viewport render.
    scrubToIndex(0);
    await nextFrame();
    await nextFrame();

    let rec: TimelapseRecorder;
    try {
      rec = await createTimelapseRecorder({
        source,
        watermark: watermarked ? WATERMARK : null,
        onProgress: (p) => setGifProgress(p),
        // Speech bubbles are DOM, and a canvas stream cannot see them: without
        // this the export of a replay containing speech came out silent — the
        // drawing was there, the words were not.
        overlay: (c, w) => drawSpeechBubbles(c, {
          speeches: Array.from(speechesRef.current.entries())
            .map(([pointId, sp]) => ({ pointId, ...sp })),
          points: renderedPointsRef.current,
          viewport: viewportRef.current,
          windowSize: windowSizeRef.current,
          scale: w / Math.max(1, windowSizeRef.current.width),
        }),
      });
    } catch {
      toastError(t('history.recordUnsupported'));
      if (savedViewport.current) { setViewport(savedViewport.current); savedViewport.current = null; }
      return;
    }

    recorderRef.current  = rec;
    recordingRef.current = true;
    setRecording(true);
    rec.start();
    setIsPlaying(true);

    trackEvent('history_record', {
      format: rec.format,
      area: recordArea ? 'selection' : 'viewport',
      from: 'history',
    });
  }, [loaded, events.length, recordArea, viewport, fitViewportToArea, scrubToIndex, watermarked, t]);

  const recordUnsupported = !canRecordWebM(); // gif fallback still works, just informs UI

  return (
    <div className="history-page">
      {/* Canvas */}
      <WorldCanvas
        ref={worldRef}
        points={renderedPoints}
        viewport={viewport}
        width={windowSize.width}
        height={windowSize.height}
        onViewportChange={setViewport}
        preserveDrawingBuffer
      />

      {/* Loading banner. The inline counter is legible on desktop but easy to
          miss on a phone, where it wraps into a corner of a crowded bar. */}
      {loading && (
        <div className="history-loading-banner" role="status" aria-live="polite">
          <span className="history-loading-banner__spinner" />
          {t('history.loadingEvents', { n: loadProgress ?? 0 })}
        </div>
      )}

      {/* Speech bubbles replayed from history */}
      <SpeechBubbles
        speeches={speeches}
        points={renderedPoints}
        viewport={viewport}
        windowSize={windowSize}
      />

      {/* Area-selection overlay (captures pointer drag, disables pan beneath) */}
      {selecting && (
        <div
          className="history-select-overlay"
          onPointerDown={onSelPointerDown}
          onPointerMove={onSelPointerMove}
          onPointerUp={onSelPointerUp}
        >
          <div className="history-select-hint">{t('history.selectAreaHint')}</div>
          {selDrag && (
            <div
              className="history-select-rect"
              style={{
                left:   Math.min(selDrag.x0, selDrag.x1),
                top:    Math.min(selDrag.y0, selDrag.y1),
                width:  Math.abs(selDrag.x1 - selDrag.x0),
                height: Math.abs(selDrag.y1 - selDrag.y0),
              }}
            />
          )}
        </div>
      )}

      {/* Persistent frame of the selected recording window (cells are centred
          on integer coords, so the box spans ±0.5 around the bounds). Hidden
          while recording — overlays are HTML and never reach the video, but a
          border pinned to the screen edges would just be noise. */}
      {recordArea && !recording && (
        <div
          className="history-record-frame"
          style={{
            left:   (recordArea.x_min - 0.5 - viewport.x) * viewport.zoom + windowSize.width / 2,
            top:    -(recordArea.y_max + 0.5 - viewport.y) * viewport.zoom + windowSize.height / 2,
            width:  (recordArea.x_max - recordArea.x_min + 1) * viewport.zoom,
            height: (recordArea.y_max - recordArea.y_min + 1) * viewport.zoom,
          }}
        >
          <span
            className="history-record-frame__label"
            title={t('history.frameMoveHint')}
            onPointerDown={onFrameHandleDown('move')}
            onPointerMove={onFrameHandleMove}
            onPointerUp={onFrameHandleUp}
            onPointerCancel={onFrameHandleUp}
          >
            ✥ ⏺ {recordArea.x_max - recordArea.x_min + 1}×{recordArea.y_max - recordArea.y_min + 1}
          </span>
          {(['nw', 'n', 'ne', 'w', 'e', 'sw', 's', 'se'] as const).map(h => (
            <div
              key={h}
              className={`history-record-frame__handle history-record-frame__handle--${h}`}
              onPointerDown={onFrameHandleDown(h)}
              onPointerMove={onFrameHandleMove}
              onPointerUp={onFrameHandleUp}
              onPointerCancel={onFrameHandleUp}
            />
          ))}
        </div>
      )}

      {/* Recording indicator */}
      {recording && (
        <div className="history-rec-indicator">
          <span className="history-rec-dot" />
          {gifProgress !== null
            ? t('history.encoding', { pct: Math.round(gifProgress * 100) })
            : t('history.recording')}
        </div>
      )}

      {/* Top-left: title + current time + coords + share */}
      <div className="history-page__hud history-page__hud--top-left">
        <div className="history-hud-info history-hud-info--title">
          {t('history.playerTitle')}
        </div>
        <div className="history-hud-info">
          <span className="history-hud-label">{t('history.timeLabel')}</span>
          <b>{currentTime.toLocaleString()}</b>
        </div>
        <div className="history-hud-info">
          <span className="history-hud-label">{t('history.viewLabel')}</span>
          <b>{Math.round(viewport.x)}, {Math.round(viewport.y)}</b>
          <span className="history-hud-label"> ×{viewport.zoom.toFixed(1)}</span>
        </div>
        <button className="btn-transparent history-share-btn" onClick={handleShare}>
          {shareCopied ? t('common.copied') : t('common.share')}
        </button>
      </div>

      {/* Top-right: standard nav menu */}
      <div className="history-page__hud history-page__hud--top-right">
        <AppNav currentPage="history" />
      </div>

      {/* Bottom dock: collapsible setup drawer + always-on transport bar */}
      <div className="history-dock">

        {/* ── Setup drawer ──────────────────────────────────────── */}
        {setupOpen && (
          <div className="history-dock__setup">
            {/* Time range */}
            <div className="history-setup__group history-setup__timerange">
              <label className="history-controls__label">{t('history.fromLabel')}</label>
              <input
                type="datetime-local"
                className="history-controls__input"
                value={toDatetimeLocal(fromTime)}
                onChange={e => { setFromTime(fromDatetimeLocal(e.target.value)); setLoaded(false); }}
              />
              <label className="history-controls__label">{t('history.toLabel')}</label>
              <input
                type="datetime-local"
                className="history-controls__input"
                value={toDatetimeLocal(toTime)}
                onChange={e => { setToTime(fromDatetimeLocal(e.target.value)); setLoaded(false); }}
              />
              <button
                className={`btn-transparent history-controls__load-btn${stale ? ' history-controls__load-btn--stale' : ''}`}
                onClick={() => loadData()}
                disabled={loading}
              >
                {loading ? t('history.loadingBtn') : stale ? t('history.staleReload') : loaded ? t('history.reloadBtn') : t('history.loadBtn')}
              </button>
              {error && <span className="px-error history-controls__error">{error}</span>}
            </div>

            {/* Mode + speed */}
            <div className="history-setup__group">
              <div className="history-controls__mode-group">
                <label className="history-controls__label">{t('history.modeLabel')}</label>
                <button
                  className={`btn-transparent history-controls__mode-btn${mode === 'fixed' ? ' history-controls__mode-btn--active' : ''}`}
                  onClick={() => { setMode('fixed'); setSpeed(5); setIsPlaying(false); }}
                >
                  {t('history.fixedMode')}
                </button>
                <button
                  className={`btn-transparent history-controls__mode-btn${mode === 'realtime' ? ' history-controls__mode-btn--active' : ''}`}
                  onClick={() => { setMode('realtime'); setSpeed(1); setIsPlaying(false); }}
                >
                  {t('history.realtimeMode')}
                </button>
              </div>
              <div className="history-controls__speed-group">
                <label className="history-controls__label">
                  {mode === 'fixed' ? t('history.evtPerS') : t('history.speedLabel')}
                </label>
                <select
                  className="history-controls__select"
                  value={speed}
                  onChange={e => setSpeed(parseFloat(e.target.value))}
                >
                  {speedOptions.map(s => (
                    <option key={s} value={s}>
                      {mode === 'fixed' ? `${s}` : `${s}×`}
                    </option>
                  ))}
                </select>
              </div>
              <label className="history-controls__label history-autoplay" title={t('history.autoplayHint')}>
                <input
                  type="checkbox"
                  checked={autoPlay}
                  onChange={e => setAutoPlay(e.target.checked)}
                />
                {t('history.autoplayLabel')}
              </label>
            </div>

            {/* Record options */}
            <div className="history-setup__group history-setup__record">
              <label className="history-controls__label">{t('history.recordArea')}</label>
              <button
                className={`btn-transparent history-controls__mode-btn${selecting ? ' history-controls__mode-btn--active' : ''}`}
                onClick={() => { setSelecting(s => !s); setSelDrag(null); }}
                disabled={recording}
              >
                {recordArea ? t('history.reselectArea') : t('history.selectArea')}
              </button>
              {recordArea ? (
                <span className="history-area-badge">
                  {recordArea.x_max - recordArea.x_min + 1}×{recordArea.y_max - recordArea.y_min + 1}
                  <button className="history-area-clear" onClick={() => setRecordArea(null)} disabled={recording}>×</button>
                </span>
              ) : (
                <span className="history-controls__label">{t('history.recordViewport')}</span>
              )}
              {recordUnsupported && (
                <span className="history-controls__label history-record-note">{t('history.recordGifNote')}</span>
              )}
            </div>
          </div>
        )}

        {/* ── Transport bar (always visible) ────────────────────── */}
        <div className="history-dock__transport">
          <button
            className="btn-transparent history-controls__play-btn"
            onClick={handleReset}
            disabled={!loaded}
            title={t('history.resetTitle')}
          >
            ⏮
          </button>
          <button
            className="btn-transparent history-controls__play-btn history-controls__play-btn--primary"
            onClick={togglePlay}
            title={isPlaying ? t('history.pauseTitle') : t('history.playTitle')}
          >
            {isPlaying ? '⏸' : '▶'}
          </button>

          <div className="history-scrubber-wrap">
            {histView ? (
              <div className="history-activity-wrap">
                <canvas
                  ref={histCanvasRef}
                  className={`history-activity${histLoading ? ' history-activity--fetching' : ''}${histTall ? ' history-activity--tall' : ''}`}
                  title={t('history.activityHint')}
                  onPointerDown={onHistDown}
                  onPointerMove={onHistMove}
                  onPointerUp={onHistUp}
                  onPointerCancel={onHistUp}
                  onDoubleClick={e => {
                    // Desktop mirror of the long press: summon the band here.
                    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    const v = histViewRef.current;
                    if (!v) return;
                    placeBandAt(v.from + ((e.clientX - r.left) / r.width) * (v.to - v.from));
                  }}
                />
                <button
                  className="btn-transparent history-activity-toggle"
                  onClick={toggleHistTall}
                  title={histTall ? t('history.timelineCollapse') : t('history.timelineExpand')}
                >
                  {histTall ? '⌄' : '⌃'}
                </button>
              </div>
            ) : histLoading ? (
              <div className="history-activity history-activity--loading" title={t('history.activityLoading')} />
            ) : null}
            <div className="history-scrubber-row">
            <span className="history-controls__timestamp">{fromTime.toLocaleTimeString()}</span>
            <input
              type="range"
              className={`history-controls__scrubber${loading ? ' history-controls__scrubber--loading' : ''}`}
              min={0}
              max={1000}
              value={Math.round(progress * 1000)}
              onChange={handleScrubberChange}
              disabled={!loaded || events.length === 0}
            />
            <span className="history-controls__timestamp">{toTime.toLocaleTimeString()}</span>
            </div>
          </div>

          <span className="history-controls__event-count">
            {loading
              ? t('history.loadingEvents', { n: loadProgress ?? 0 })
              : loaded ? t('history.events', { index: eventIndex, total: events.length }) : t('history.eventsEmpty')}
            {truncatedTotal !== null && (
              <span className="history-controls__truncated" title={t('history.truncatedHint')}>
                {' '}{t('history.truncated', { total: truncatedTotal })}
              </span>
            )}
            {stale && <span className="history-controls__stale-mark"> ⟳</span>}
          </span>

          <button
            className={`btn-transparent history-rec-btn${recording ? ' history-rec-btn--active' : ''}`}
            onClick={() => (recording ? finishRecording(true) : startRecording())}
            disabled={!recording && (!loaded || events.length === 0)}
            title={recording ? t('history.stopTitle') : t('history.recordTitle')}
          >
            {recording ? `⏹ ${t('history.stop')}` : `⏺ ${t('history.record')}`}
          </button>

          <button
            className={`btn-transparent history-setup-toggle${setupOpen ? ' history-setup-toggle--active' : ''}`}
            onClick={() => setSetupOpen(o => !o)}
            title={t('history.setup')}
          >
            ⚙
          </button>
        </div>
      </div>
    </div>
  );
};
